import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import Marquee from "@/components/Marquee";
import { FadeUp, Overline } from "@/components/Reveal";
import { SEO } from "@/lib/seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const SECTIONS = [
  {
    n: "01", title: "The Australian education system",
    text: "Australian universities offer bachelor's, master's and doctoral degrees, with most undergraduate degrees taking three to four years. The academic year typically runs from late February to November, split into two semesters, and qualifications sit within the Australian Qualifications Framework — making them structured and internationally comparable.",
  },
  {
    n: "02", title: "The Group of Eight",
    text: "The Group of Eight is a coalition of Australia's leading research-intensive universities. They are known for research output, comprehensive course offerings and international recognition. But the Go8 is not a ranking — many other Australian universities offer outstanding programs, and the right choice depends on your course, city and goals.",
  },
  {
    n: "03", title: "Student cities",
    text: "Australia's major student cities each have a distinct character. Sydney and Melbourne are large, global and fast-paced; Brisbane and Perth offer warmth and space; Adelaide and Canberra are smaller, more affordable and easy to navigate. Where you live shapes your experience as much as what you study.",
  },
  {
    n: "04", title: "Cost of living",
    text: "Living costs vary significantly by city and lifestyle. Accommodation is usually the largest expense, followed by food and transport. Rather than relying on averages, build a personal budget using official university cost guides — our advisors can help you structure one for your target city.",
  },
  {
    n: "05", title: "Graduate opportunities",
    text: "Australia offers post-study work pathways for eligible graduates, and many programs include internships or industry placements. Graduate outcomes vary by field and individual — we encourage students to research their specific discipline rather than rely on broad promises.",
  },
  {
    n: "06", title: "The international student experience",
    text: "Australia hosts one of the largest international student communities in the world. Universities provide orientation programs, academic support, wellbeing services and dedicated international student advisers. Cities are multicultural, and student communities from South Asia and beyond are well established.",
  },
];

const FAQS = [
  { q: "Is Australia good for international students?", a: "Australia is one of the most popular study destinations globally, with a large international student community, strong student protections and globally recognised qualifications. Whether it's right for you depends on your course, budget and goals — that's exactly what we help you work through." },
  { q: "When are the main intakes?", a: "Most Australian universities have two major intakes — typically February and July — with some courses offering additional start dates. Application deadlines usually fall several months before the intake." },
  { q: "Can I work while studying?", a: "Student visa holders are generally permitted to work a limited number of hours during study periods, with rules set by the Australian government. Always check the current official visa conditions, as these can change." },
  { q: "Do I need IELTS or another English test?", a: "Most universities require evidence of English proficiency, with accepted tests and required scores varying by university and course. Check the admissions page of each course you're considering." },
  { q: "Is Vitaliah affiliated with any university?", a: "No. Vitaliah is an independent guidance platform. We don't claim partnerships or affiliations — we help you understand publicly available information and navigate your options clearly." },
];

export default function WhyAustralia() {
  return (
    <div data-testid="why-australia-page">
      <SEO title="Why Study in Australia? | Vitaliah" description="Explore the Australian education system, Group of Eight universities, student cities, cost of living, graduate opportunities and the international student experience." />
      <Navbar />
      <PageHero
        overline="The Destination"
        title="Why study in Australia?"
        subtitle="A clear-eyed look at what Australia offers international students — the education system, the cities, the costs and the experience."
      />
      <Marquee dark={false} />
      <section className="py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 space-y-20">
          {SECTIONS.map((s, i) => (
            <FadeUp key={s.n} delay={0.05}>
              <div data-testid={`why-section-${s.n}`} className={`grid lg:grid-cols-12 gap-10 border-t border-border pt-14 ${i % 2 ? "" : ""}`}>
                <div className="lg:col-span-4 flex items-start gap-6">
                  <span className="font-serif italic text-4xl text-gold">{s.n}</span>
                  <h2 className="font-serif text-3xl font-medium text-navy leading-snug">{s.title}</h2>
                </div>
                <p className="lg:col-span-8 text-navy/70 leading-relaxed text-lg max-w-3xl">{s.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>
      <section id="faqs" data-testid="why-faqs" className="py-28 bg-sand border-t border-border">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <FadeUp><Overline>Common questions</Overline></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-medium tracking-tight text-navy">Frequently asked questions</h2>
          </FadeUp>
          <FadeUp delay={0.2} className="mt-12">
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="bg-white border border-border px-7">
                  <AccordionTrigger className="text-left font-serif text-xl font-medium text-navy hover:no-underline py-6">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-navy/70 leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
        </div>
      </section>
      <FinalCTA />
      <Footer />
    </div>
  );
}
